import { baseApi } from './baseApi';
import { ServiceTypes } from '../constants';

export interface MetadataSetting {
  id: number;
  key: string;
  allowed_values: string[] | null;
}

export interface MetadataUsage {
  key: string;
  usage_count: number;
}

interface MetadataSettingFromApi {
  ID: number;
  key: string;
  allowed_values: string[] | null;
}

interface MetadataUsageFromApi {
  key: string;
  usage_count: number;
}

const mapMetadataToStore = (item: MetadataSettingFromApi): MetadataSetting => ({
  id: item.ID,
  key: item.key,
  allowed_values: item.allowed_values
});

const mapMetadataUsageToStore = (item: MetadataUsageFromApi): MetadataUsage => ({
  key: item.key,
  usage_count: item.usage_count ?? 0,
});

const mapMetadataFieldsToPayload = (fields: AssetMetadataField[]): MetadataFieldPayload =>
  fields.reduce<MetadataValueMap>((acc, field) => {
    if (typeof field.value === 'string' && field.value.length > 0) {
      acc[field.key] = field.value;
    }
    return acc;
  }, {});

export const metadataService = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    getMetadata: builder.query<MetadataSetting[], void>({
      query: () => '/metadata',
      transformResponse: (result: INgApiResponse<MetadataSettingFromApi[]>) => (result.data || []).map(mapMetadataToStore),
      providesTags: (result) => [
        { type: ServiceTypes.METADATA, id: 'LIST' },
        ...((result || []).filter((item) => item.id !== undefined).map((item) => ({
          type: ServiceTypes.METADATA as const,
          id: item.id!,
        })) || []),
      ],
    }),
    getMetadataUsage: builder.query<MetadataUsage[], void>({
      query: () => '/metadata/usage',
      transformResponse: (result: INgApiResponse<MetadataUsageFromApi[]>) =>
        (result.data || []).map(mapMetadataUsageToStore),
      providesTags: () => [{ type: ServiceTypes.METADATA, id: 'LIST' }],
    }),
    createMetadata: builder.mutation<
      MetadataSetting,
      { data: Pick<MetadataSetting, 'key' | 'allowed_values'> }
    >({
      query: ({ data }) => ({
        url: '/metadata',
        method: 'POST',
        body: data,
      }),
      transformResponse: (result: INgApiResponse<MetadataSettingFromApi>) => mapMetadataToStore(result.data),
      invalidatesTags: () => [{ type: ServiceTypes.METADATA, id: 'LIST' }],
    }),
    updateMetadata: builder.mutation<
      MetadataSetting,
      { key: string; data: Pick<MetadataSetting, 'key' | 'allowed_values'> }
    >({
      query: ({ key, data }) => ({
        url: `/metadata/${encodeURIComponent(key)}`,
        method: 'PUT',
        body: data,
      }),
      transformResponse: (result: INgApiResponse<MetadataSettingFromApi>) => mapMetadataToStore(result.data),
      invalidatesTags: () => [{ type: ServiceTypes.METADATA, id: 'LIST' }],
    }),
    updateAssetItemMetadata: builder.mutation<IApiResponse<any>, { assetId: number; data: AssetMetadataField[] }>({
      query: ({ assetId, data }) => ({
        url: `/assets/${assetId}/metadata`,
        method: 'PUT',
        body: {
          Metadata: mapMetadataFieldsToPayload(data),
        },
      }),
      invalidatesTags: () => [{ type: 'Folders' }, { type: 'Assets' }, { type: 'Presentations' }],
    }),
    updatePresentationItemMetadata: builder.mutation<
      INgApiResponse<PresentationFromApi>,
      { presentationId: number; data: AssetMetadataField[] }
    >({
      query: ({ presentationId, data }) => ({
        url: `/presentations/${presentationId}/metadata`,
        method: 'PUT',
        body: {
          Metadata: mapMetadataFieldsToPayload(data),
        },
      }),
      invalidatesTags: () => [{ type: 'Folders' }, { type: 'Presentations' }],
    }),
    deleteMetadata: builder.mutation<void, { key: string }>({
      query: ({ key }) => ({
        url: `/metadata/${encodeURIComponent(key)}`,
        method: 'DELETE',
      }),
      invalidatesTags: (result, error, { key }) => [
        { type: ServiceTypes.METADATA, id: 'LIST' },
        { type: ServiceTypes.METADATA, id: key },
      ],
    }),
  }),
});

export const {
  useGetMetadataQuery,
  useGetMetadataUsageQuery,
  useCreateMetadataMutation,
  useUpdateMetadataMutation,
  useUpdateAssetItemMetadataMutation,
  useUpdatePresentationItemMetadataMutation,
  useDeleteMetadataMutation,
} = metadataService;
